import { useCallback, useEffect, useState } from "react";
import { api, fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { MessageCircleHeart } from "lucide-react";

const STATUS = {
  pending: { label: "Da rispondere", cls: "text-amber-300 border-amber-500/40" },
  answered: { label: "Risposta inviata · attende replica", cls: "text-emerald-300 border-emerald-500/40" },
  user_replied: { label: "Da rispondere · risposta finale", cls: "text-amber-300 border-amber-500/40" },
  closed: { label: "Conclusa", cls: "text-purple-300/60 border-[#4a2175]" },
};

const FILTERS = [
  { v: "open", label: "Da rispondere" },
  { v: "answered", label: "Risposta inviata" },
  { v: "closed", label: "Concluse" },
  { v: "all", label: "Tutte" },
];

const matchFilter = (q, f) =>
  f === "all" ? true
  : f === "open" ? q.status === "pending" || q.status === "user_replied"
  : f === "answered" ? q.status === "answered"
  : q.status === "closed";

export default function AdminVirginia() {
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("open");

  const load = useCallback(() => {
    api.get("/admin/virginia").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const answer = async (id) => {
    const text = (drafts[id] || "").trim();
    if (text.length < 2) return;
    setBusy(id);
    try {
      await api.post(`/admin/virginia/${id}/answer`, { text });
      toast.success("Risposta inviata.");
      setDrafts({ ...drafts, [id]: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const shown = (items || []).filter((q) => matchFilter(q, filter));
  const openCount = (items || []).filter((q) => q.status === "pending" || q.status === "user_replied").length;

  return (
    <div data-testid="admin-virginia-section">
      <h2 className="font-display text-2xl text-amber-100 mb-2">Domande per Virginia</h2>
      <p className="text-xs text-purple-300/60 mb-6">
        Le domande personali degli abbonati: una domanda, una tua risposta, al massimo una loro replica con la tua risposta finale.
      </p>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="virginia-admin-filters">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`virginia-filter-${f.v}`}
            className={`text-xs rounded-full px-4 py-1.5 border transition-colors ${
              filter === f.v
                ? "border-amber-500/60 text-amber-200 bg-amber-500/10"
                : "border-[#4a2175] text-purple-300/60 hover:text-amber-200"
            }`}
          >
            {f.label}{f.v === "open" && openCount > 0 ? ` (${openCount})` : ""}
          </button>
        ))}
      </div>

      {items === null ? null : shown.length === 0 ? (
        <p className="text-purple-300/50 text-sm" data-testid="virginia-admin-empty">
          {items.length === 0 ? "Nessuna domanda ricevuta finora." : "Nessuna domanda in questo stato."}
        </p>
      ) : (
        <div className="space-y-4" data-testid="virginia-admin-list">
          {shown.map((q) => {
            const st = STATUS[q.status] || STATUS.pending;
            return (
              <div key={q.id} className="mystic-card p-6" data-testid={`virginia-admin-item-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <p className="text-sm text-purple-100/85">
                    <span className="text-amber-200 font-medium">{q.user_name}</span>
                    {q.user_email && <span className="text-purple-300/50 text-xs ml-2">{q.user_email}</span>}
                    <span className="text-purple-300/50 text-xs ml-3">{fmtDateTime(q.created_at)}</span>
                  </p>
                  <span className={`text-[10px] uppercase tracking-wider border rounded-full px-3 py-1 ${st.cls}`}>{st.label}</span>
                </div>
                <div className="space-y-3">
                  {q.messages.map((m, i) => (
                    <div key={i} className={`rounded-xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "virginia"
                        ? "bg-amber-500/10 border border-amber-500/25 text-amber-50"
                        : "bg-[#0f0617] border border-[#4a2175] text-purple-100/85"
                    }`}>
                      <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mb-1">
                        {m.role === "virginia" ? "Tu (Virginia)" : q.user_name}
                        <span className="ml-2 normal-case tracking-normal">{fmtDateTime(m.at)}</span>
                      </p>
                      <p className="whitespace-pre-line">{m.text}</p>
                    </div>
                  ))}
                </div>
                {(q.status === "pending" || q.status === "user_replied") && (
                  <div className="mt-4 space-y-3">
                    <textarea
                      value={drafts[q.id] || ""}
                      onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })}
                      rows={4}
                      maxLength={1000}
                      placeholder={q.status === "pending" ? "Scrivi la tua risposta personale…" : "Scrivi la risposta finale (la conversazione si chiuderà)…"}
                      data-testid={`virginia-answer-input-${q.id}`}
                      className="input-dark"
                    />
                    <button
                      onClick={() => answer(q.id)}
                      disabled={busy === q.id || (drafts[q.id] || "").trim().length < 2}
                      data-testid={`virginia-answer-btn-${q.id}`}
                      className="gold-btn px-5 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
                    >
                      <MessageCircleHeart className="w-4 h-4" />
                      {busy === q.id ? "Invio…" : q.status === "pending" ? "Invia risposta" : "Invia risposta finale"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
